// src/components/RevenueHeader.jsx
import React from 'react'

/**
 * RevenueHeader
 *
 * Props:
 * - accumFee: number | undefined   // total earned fees in USD
 * - accumFeeUnavailable: boolean   // when fees aren't available, show a dash
 * - firstPaintDone: boolean        // Phase-1 initial paint complete
 * - phase2FinalDone: boolean       // Phase-2 merged (final) data complete
 * - createHref?: string            // optional: where the "create coin" CTA should link
 */

export default function RevenueHeader({
  icon,
  accumFee,
  accumFeeUnavailable,
  firstPaintDone,
  phase2FinalDone
}) {
  const showSkeleton =
    !firstPaintDone ||
    (!phase2FinalDone && accumFee == null && !accumFeeUnavailable)

  return (
    <div className="rounded-lg border border-[#2a2d36] bg-[#171a21] px-4 py-3 grid md:grid-cols-[2fr_minmax(260px,1fr)] items-center">
      {/* Left: explainer */}
      <div className="hidden sm:flex items-center gap-3">
        <span
          aria-hidden="true"
          className="inline-block h-2.5 w-2.5 mt-1 rounded-full bg-[#7CFC98]"
        />
        <div className="min-w-0">
          <div className="text-[#f8ffe8] font-semibold leading-5">
            coin creators earn{' '}
            <span className="text-[#7CFC98]">lifetime revenues</span>
          </div>
          <div className="text-[12px] text-[#f8ffe8]/70 leading-4">
            every trade pays you; liquidity stays locked
          </div>
        </div>
      </div>

      {/* Right: total fees + CTA */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="min-w-0">
            <div className="text-[12px] text-[#f8ffe8]/70 leading-4">
              total earned fees
            </div>
            {showSkeleton ? (
              <div className="mt-1 h-5 w-20 rounded bg-white/10 animate-pulse" />
            ) : (
              <div className="mt-0.5 text-[18px] font-bold text-[#7CFC98] tabular-nums">
                {accumFeeUnavailable
                  ? '—'
                  : `$${Number(accumFee ?? 0).toFixed(2)}`}
              </div>
            )}
          </div>
        </div>
        <span className="hc-stroke-ring relative inline-block">
          <a
            href="/create"
            className="relative inline-flex items-center justify-center gap-2 rounded-lg text-black bg-[#7CFC98] hover:bg-[#b1ffcb] px-5 py-2 text-[13px] font-semibold shadow-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-green-400 w-full md:w-auto"
          >
            <span className="relative z-10">create coin</span>
          </a>
          {/* SVG border overlay */}
          <svg
            viewBox="0 0 100 40"
            preserveAspectRatio="none"
            aria-hidden="true"
            className="absolute inset-0 w-full h-full"
          >
            <rect
              x="1"
              y="1"
              width="98"
              height="38"
              rx="8"
              pathLength="100"
              fill="none"
              className="hc-stroke-green"
            />
            <rect
              x="1"
              y="1"
              width="98"
              height="38"
              rx="8"
              pathLength="100"
              fill="none"
              className="hc-stroke-pink"
            />
          </svg>
        </span>
      </div>
    </div>
  )
}
